<!--
  THE END OF A PRACTICE RUN (spec 53 sections 5.3 and 8.2), mounted beside
  TourHost in the shell. Renders nothing until a run ends, and does nothing
  at all outside a practice fleet (a training lease).

  When TourHost ends a training module it dispatches `lw:training-end` with
  the step log and the page's own grade. This:

    1. posts the log to the practice fleet's POST /api/training/practice/
       complete, which grades it again and checks the evidence in the
       practice tenant (the server has the last word), answering a signed
       receipt, `passed` true or false (a missing piece of evidence answers
       409 and no receipt);
    2. carries the receipt to production's POST /api/training/receipts,
       keeping it in `lw:training` until it lands (receipts.js), and tries
       again on the next page load;
    3. celebrates: the module, the course, the account opening when
       production says `cleared`, or "Almost there" when it didn't pass.

  A run that was left (Exit, closing the tab) stays open (4.6): nothing is
  posted for it.
-->
<template>
  <LwCelebration
    v-if="celebration"
    :key="celebration.key"
    :state="celebration.state"
    :title="celebration.title"
    :unlocks="celebration.unlocks"
    :own-steps="celebration.ownSteps"
    :total-steps="celebration.totalSteps"
    :next-title="celebration.next ? celebration.next.title : ''"
    :course-line="celebration.courseLine"
    :missing="celebration.missing"
    :minutes="celebration.minutes"
    :passed-titles="celebration.passedTitles"
    :account-url="`${origin}/`"
    @next="practiseModule(celebration.next.id)"
    @back="backToTraining"
    @retry="practiseModule(celebration.module)"
    @watch="watchVideo(celebration.module)"
  />
</template>

<script>
import LwCelebration from './lw/LwCelebration.vue';
import { COURSES } from '../training/courses.js';
import { nextChapter, isPassed } from '../training/catalog.js';
import { clearRun, readRun } from '../training/stepLog.js';
import { practise } from '../training/practise.js';
import { loadTour } from '../tours/registry';
import {
  accountOrigin,
  addPending,
  capturedForServer,
  isFinalRefusal,
  passedHere,
  pendingReceipts,
  rememberPassed,
  removePending,
  sendReceipt,
} from '../training/receipts.js';

// A course's closing line (8.2).
const COURSE_LINES = Object.freeze({
  dispatch: "You've taken a load from a phone call to delivered.",
  billing: "You've taken a delivered load all the way to paid.",
});

let celebrationSeq = 0;

/**
 * The practice server's answer with its grade at the top level: it may nest
 * the grade under `result` ({receipt, result: {passed, steps, missing}}).
 */
function flatten(answer) {
  const body = answer && typeof answer === 'object' ? answer : {};
  const result = body.result && typeof body.result === 'object' ? body.result : {};
  return {
    ...body,
    passed: body.passed !== undefined ? body.passed : result.passed,
    steps: Array.isArray(body.steps) ? body.steps : result.steps,
    missing:
      Array.isArray(body.missing) && body.missing.length ? body.missing : result.missing || [],
  };
}

export default {
  name: 'TrainingRunHost',
  components: { LwCelebration },
  props: {
    // Where receipts are posted. A seam for tests; the browser's fetch.
    fetchImpl: { type: Function, default: null },
  },
  data() {
    return { celebration: null };
  },
  computed: {
    lease() {
      return (this.$store && this.$store.getters && this.$store.getters.trainingLease) || null;
    },
    origin() {
      return accountOrigin(this.lease);
    },
  },
  watch: {
    // The organization (and so the lease) lands after the shell mounts.
    lease(now) {
      if (now) this.resume();
    },
  },
  mounted() {
    window.addEventListener('lw:training-end', this.onRunEnd);
    if (this.lease) this.resume();
  },
  beforeUnmount() {
    window.removeEventListener('lw:training-end', this.onRunEnd);
  },
  methods: {
    onRunEnd(event) {
      const detail = (event && event.detail) || {};
      if (!this.lease || detail.reason !== 'completed' || !detail.module) return;
      this.finish(detail);
    },

    /**
     * After a reload: a finished run whose log never reached the practice
     * server is posted now, and receipts production never took are sent
     * again (5.3 step 5).
     */
    async resume() {
      if (this.resuming) return;
      this.resuming = true;
      try {
        const run = readRun();
        if (run && run.finished) {
          await this.finish({ ...run, result: null });
        }
        for (const pending of pendingReceipts()) {
          const answer = await this.deliver(pending.receipt);
          if (answer && answer.cleared) {
            await this.celebrateAccount(pending.module);
            break;
          }
        }
      } finally {
        this.resuming = false;
      }
    },

    async finish(run) {
      let graded;
      try {
        const res = await this.$http.post('/training/practice/complete', {
          module: run.module,
          version: run.version,
          startedAt: run.startedAt,
          steps: run.steps,
          // What the walkthrough captured (the load it made, the driver it
          // opened), so the evidence check looks at the trainee's own records.
          captured: capturedForServer(run.captured),
        });
        graded = flatten(res.data);
      } catch (err) {
        const res = err && err.response;
        const body = (res && res.data) || {};
        if (res && res.status === 409 && body.error_code === 'TRAINING_EVIDENCE_MISSING') {
          graded = { ...flatten(body), passed: false };
        } else {
          // Kept in `lw:training`: the next page load posts it again.
          this.notify(
            "Ladeway couldn't check this practice run. It's kept, and goes again when you reload the page."
          );
          return;
        }
      }
      clearRun();

      const catalog = await this.loadCatalog();
      const module = catalog.find(m => m.id === run.module) || { id: run.module };
      const steps = Array.isArray(graded.steps)
        ? graded.steps
        : (run.result && run.result.steps) || [];

      if (!graded.passed) {
        // A run that graded not passed is signed too (`passed: false`), so
        // production closes the attempt `not_passed` (4.6). Kept like a
        // pass's until it lands; it never opens the account.
        if (typeof graded.receipt === 'string' && graded.receipt) {
          addPending({ receipt: graded.receipt, module: run.module });
          await this.deliver(graded.receipt);
        }
        const missing =
          (Array.isArray(graded.missing) && graded.missing.length && graded.missing) ||
          (run.result && run.result.missing) ||
          [];
        this.show({
          state: 'not-passed',
          module: run.module,
          title: module.title || '',
          missing,
          minutes: Number(module.minutes) || 0,
        });
        return;
      }

      rememberPassed(run.module);
      let answer = null;
      if (typeof graded.receipt === 'string' && graded.receipt) {
        addPending({ receipt: graded.receipt, module: run.module });
        answer = await this.deliver(graded.receipt);
      }
      if (answer && answer.cleared) {
        await this.celebrateAccount(run.module, catalog);
        return;
      }

      const definition = await this.definitionOf(run.tourId || run.module);
      const training = (definition && definition.training) || {};
      const lastChapter =
        module.course &&
        COURSES[module.course] &&
        module.chapter === COURSES[module.course].chapters;
      const next = lastChapter ? null : nextChapter(module, catalog);
      this.show({
        state: lastChapter ? 'course' : 'module',
        module: run.module,
        title: module.title || (definition && definition.title) || '',
        unlocks: training.unlocks || '',
        ownSteps: steps.filter(s => s && s.mode === 'self').length,
        totalSteps: steps.length,
        next: next ? { id: next.id, title: next.title } : null,
        courseLine: lastChapter ? COURSE_LINES[module.course] || '' : '',
      });
    },

    /**
     * One receipt to production. Answers production's reply, or null when it
     * did not land (kept for later) or was refused for good (dropped).
     */
    async deliver(receipt) {
      try {
        const answer = await sendReceipt(receipt, this.origin, this.fetchImpl || undefined);
        removePending(receipt);
        return answer;
      } catch (err) {
        if (isFinalRefusal(err)) removePending(receipt);
        return null;
      }
    },

    async celebrateAccount(moduleId, known) {
      const catalog = known || (await this.loadCatalog());
      // The practice fleet's own passes (the lease's `passed`), this tab's,
      // and the one that opened the account. A pass from a fleet since
      // reclaimed is in none of them; production's record still has it.
      const leasePassed = this.lease && Array.isArray(this.lease.passed) ? this.lease.passed : [];
      const here = new Set([...leasePassed, ...passedHere(), moduleId]);
      const passedTitles = catalog
        .filter(m => isPassed(m) || here.has(m.id))
        .map(m => m.title)
        .filter(Boolean);
      this.show({ state: 'account', module: moduleId, passedTitles });
    },

    show(fields) {
      celebrationSeq += 1;
      this.celebration = {
        key: celebrationSeq,
        title: '',
        unlocks: '',
        ownSteps: 0,
        totalSteps: 0,
        next: null,
        courseLine: '',
        missing: [],
        minutes: 0,
        passedTitles: [],
        ...fields,
      };
    },

    async loadCatalog() {
      try {
        const res = await this.$http.get('/training/modules');
        return (res.data && Array.isArray(res.data.modules) && res.data.modules) || [];
      } catch (err) {
        return [];
      }
    },

    async definitionOf(id) {
      try {
        return await loadTour(id);
      } catch (err) {
        return null;
      }
    },

    async practiseModule(moduleId) {
      this.celebration = null;
      const outcome = await practise({ http: this.$http, router: this.$router, moduleId });
      if (!outcome.ok) {
        this.notify(
          outcome.reason === 'not-ready'
            ? "Practice for that module isn't ready yet."
            : "The practice fleet couldn't set that module up. Try again in a moment."
        );
        this.$router.push({ name: 'Training' }).catch(() => {});
      }
    },

    backToTraining() {
      this.celebration = null;
      this.$router.push({ name: 'Training' }).catch(() => {});
    },

    watchVideo(moduleId) {
      this.celebration = null;
      this.$router.push({ name: 'TrainingModule', params: { id: moduleId } }).catch(() => {});
    },

    notify(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch('toasts/notify', { tone: 'danger', message });
      }
    },
  },
};
</script>
